import React, { useState } from "react";
import "./App.css";
import {
  Mail,
  MapPin,
  Home,
  Wrench,
  Clock,
  Shield,
  Plane,
  Users,
  ChevronDown,
  ChevronUp,
  Calculator,
  FileText,
  Scissors,
  AlertTriangle,
  Banknote,
  CheckCircle,
  Quote,
  Menu,
  X,
  Phone,
} from "lucide-react";

const App: React.FC = () => {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const faqs = [
    {
      q: "How does the process work?",
      a: "The process is simple. You contact us with basic information about your property, we review the details, and then we make a fair cash offer. If the offer works for you, we move forward toward closing at a timeline that fits your situation.",
    },
    {
      q: "Do I need to make repairs before selling?",
      a: "No. We buy properties as-is, which means you do not need to make repairs, clean out the house, or spend money preparing it for sale.",
    },
    {
      q: "Are there any fees or commissions?",
      a: "No. Since we are direct buyers, there are no agent commissions or hidden fees. We keep the process straightforward so you know exactly what to expect.",
    },
    {
      q: "How fast can you close?",
      a: "In many cases, we can close quickly depending on the property, title, and the seller's needs. We can also work with your preferred timeline if you need more time before closing.",
    },
    {
      q: "What types of properties do you buy?",
      a: "We buy many types of residential properties, including houses that need repairs, inherited homes, vacant properties, rental properties, and homes where the owner simply wants a faster and easier sale.",
    },
    {
      q: "Am I obligated to accept the offer?",
      a: "No. There is no pressure and no obligation. We will present you with an offer, answer your questions, and give you time to decide if it is the right solution for you.",
    },
    {
      q: "How do you decide on the offer price?",
      a: "We look at the property's condition, location, recent comparable sales, repair needs, and current market value. Our goal is to make a fair offer that works for both sides.",
    },
    {
      q: "Why sell to Solid Offer Buyers Properties instead of listing with an agent?",
      a: "Selling to us can be a better option if you want to avoid repairs, showings, long waiting periods, commissions, or uncertainty. We provide a simpler option for homeowners who want a direct and reliable sale.",
    },
  ];

  const situations = [
    {
      icon: <AlertTriangle size={18} color="#fff" strokeWidth={1.75} />,
      title: "Avoiding foreclosure",
      desc: "Behind on payments or facing auction? Close before it hits your credit.",
    },
    {
      icon: <Scissors size={18} color="#fff" strokeWidth={1.75} />,
      title: "Divorce or separation",
      desc: "A clean, fast sale so both parties can move forward.",
    },
    {
      icon: <Plane size={18} color="#fff" strokeWidth={1.75} />,
      title: "Job relocation",
      desc: "Need to be in a new city next month? We can close on your timeline.",
    },
    {
      icon: <FileText size={18} color="#fff" strokeWidth={1.75} />,
      title: "Inherited property",
      desc: "Skip probate hassles and split proceeds easily even out of state.",
    },
    {
      icon: <Users size={18} color="#fff" strokeWidth={1.75} />,
      title: "Tired landlord",
      desc: "Bad tenants? We buy occupied properties and handle the transition.",
    },
    {
      icon: <Wrench size={18} color="#fff" strokeWidth={1.75} />,
      title: "Major repairs needed",
      desc: "Foundation, roof, plumbing we buy houses other buyers walk away from.",
    },
    // {
    //   icon: <Flame size={18} color="#fff" strokeWidth={1.75} />,
    //   title: "Fire or storm damage",
    //   desc: "We've purchased many distressed homes. No condition is too tough.",
    // },
    // {
    //   icon: <TrendingDown size={18} color="#fff" strokeWidth={1.75} />,
    //   title: "Underwater on mortgage",
    //   desc: "We work with you on short-sale and equity-light situations.",
    // },
    // {
    //   icon: <Receipt size={18} color="#fff" strokeWidth={1.75} />,
    //   title: "Tax liens or back taxes",
    //   desc: "We can settle liens at closing as part of the transaction.",
    // },
    // {
    //   icon: <Home size={18} color="#fff" strokeWidth={1.75} />,
    //   title: "Vacant property",
    //   desc: "Tired of paying for a house you're not using? Cash out and move on.",
    // },
    // {
    //   icon: <Banknote size={18} color="#fff" strokeWidth={1.75} />,
    //   title: "Financial hardship",
    //   desc: "Medical bills, debt, or sudden change we help you get unstuck.",
    // },
    // {
    //   icon: <RefreshCw size={18} color="#fff" strokeWidth={1.75} />,
    //   title: "Just want a fresh start",
    //   desc: "Whatever the reason, we close discreetly with no pressure.",
    // },
  ];

  const testimonials = [
    {
      text: "“Eddie made the whole process simple from the first call. I had a property I didn’t want to deal with anymore, and he explained everything clearly, gave me a fair offer, and kept his word until closing.”",
      name: "Michael R.",
      location: "Cleveland, OH",
      tag: "Tired Landlord",
    },
    {
      text: '"I needed to sell quickly and didn’t want to list the property or make repairs. Eddie and the team gave me a straightforward offer and helped me close without stress."',
      name: "James W.",
      location: "Tampa, FL",
      tag: "Major Repairs Needed",
    },
    {
      text: '"Solid Offer Buyers Properties helped us sell a home that needed a lot of work. Eddie was honest, responsive, and made the closing process very smooth."',
      name: "Robert & Denise K.",
      location: "Columbus, OH",
      tag: "Major Repairs Needed",
    },
    {
      text: '"I appreciated how direct and respectful Bruce was. He explained the numbers, gave us time to think, and followed through on everything."',
      name: "Karen S.",
      location: "Orlando, FL",
      tag: "Downsizing",
    },
  ];

  const compareRows = [
    ["Commissions / agent fees", "5–6% of sale price", "$0 we pay all fees"],
    ["Closing costs", "1–3% of sale price", "$0 covered by Solid Offer Buyers"],
    ["Repairs needed before sale", "Often required", "Zero sold as-is"],
    ["Cleanup / staging", "Required", "Take what you want, leave the rest"],
    // ["Showings & open houses", "Multiple", "Zero"],
    [
      "Financing falls through risk",
      "High buyer's lender approval",
      "None all cash",
    ],
    // ["Days on market", "30–90+ days", "Skip the market entirely"],
    ["Closing timeline", "45–60+ days", "As fast as 7 days"],
    // ["Inspections & contingencies", "Standard", "None"],
  ];

  return (
    <div className="app">
      <nav className="navbar">
        <div className="nav-inner">
          <a href="#hero" className="logo">
            <img
              src="/logo.png"
              alt="Solid Offer Buyers"
              className="logo-img"
            />
          </a>
          <div className="nav-links">
            <a href="#how-it-works">How It Works</a>
            {/* <a href="#formula">Cash Offer Formula</a> */}
            <a href="#compare">Compare</a>
            <a href="#situations">Situations</a>
            <a href="#faqs">FAQs</a>
          </div>
          <div className="nav-right">
            <a href="tel:2166663609" className="nav-phone">
              <Phone size={14} strokeWidth={2} /> (216) 666-3609
            </a>
            <a href="#hero-form" className="btn-primary">
              Get My Cash Offer
            </a>
          </div>
          <button
            className="nav-hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <X size={22} strokeWidth={2} />
            ) : (
              <Menu size={22} strokeWidth={2} />
            )}
          </button>
        </div>
        {menuOpen && (
          <div className="mobile-menu">
            <a href="#how-it-works" onClick={() => setMenuOpen(false)}>
              How It Works
            </a>
            {/* <a href="#formula" onClick={() => setMenuOpen(false)}>
              Cash Offer Formula
            </a> */}
            <a href="#compare" onClick={() => setMenuOpen(false)}>
              Compare
            </a>
            <a href="#situations" onClick={() => setMenuOpen(false)}>
              Situations
            </a>
            <a href="#faqs" onClick={() => setMenuOpen(false)}>
              FAQs
            </a>
            <a
              href="tel:2166663609"
              className="mobile-menu-phone"
              onClick={() => setMenuOpen(false)}
            >
              <Phone size={14} strokeWidth={2} /> (216) 666-3609
            </a>
            <a
              href="#hero-form"
              className="btn-primary mobile-menu-cta"
              onClick={() => setMenuOpen(false)}
            >
              Get My Cash Offer
            </a>
          </div>
        )}
      </nav>

      <section
        className="hero"
        id="hero"
        style={{ backgroundImage: "url('/house2.jpg')" }}
      >
        <div className="hero-overlay">
          <div className="hero-inner">
            <div className="hero-left">
              {/* <div className="rating-badge">
                <span className="stars">★★★★★</span>
                <span>4.9/5 · 180+ homeowners served</span>
              </div> */}
              <h1>
                Sell Your House{" "}
                <span className="hero-highlight">Fast for CASH.</span>{" "}
                <span className="hero-sub">Any Condition. Any Situation.</span>
              </h1>
              <p className="hero-desc">
                Solid Offer Buyers buys houses directly from homeowners
                nationwide no agents, no repairs, no fees. Get a fair,
                no-obligation cash offer in 24 hours and close on the day you
                choose.
              </p>
              <div className="hero-badges">
                <div className="badge">
                  <Clock size={14} strokeWidth={2} /> Close in as little as 7
                  days
                </div>
                <div className="badge">
                  <CheckCircle size={14} strokeWidth={2} /> Zero fees, zero
                  commissions
                </div>
                <div className="badge">
                  <Shield size={14} strokeWidth={2} /> We buy houses as-is
                </div>
              </div>
              {/* <div className="hero-image-wrapper">
                <img src="/house.jpg" alt="Family home" className="hero-house-img" />
                <div className="hero-stat left-stat">
                  <div className="stat-label">CASH PAID TO HOMEOWNERS</div>
                  <div className="stat-value">$45M+</div>
                </div>
                <div className="hero-stat right-stat">
                  <div className="stat-label">HOMES PURCHASED</div>
                  <div className="stat-value">250+</div>
                </div>
              </div> */}
            </div>
            <div className="hero-right" id="hero-form">
              <div className="form-card">
                <div className="form-header-dark">
                  <span className="form-badge-text">FREE · NO OBLIGATION</span>
                  <h2>Get your fair cash offer in 24 hours</h2>
                  <p>Tell us about your property we'll do the rest.</p>
                </div>
                <div className="form-body">
                  <div className="form-group">
                    <label>PROPERTY ADDRESS</label>
                    <input type="text" placeholder="123 Main St, Dallas, TX" />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>YOUR NAME</label>
                      <input type="text" placeholder="Full name" />
                    </div>
                    <div className="form-group">
                      <label>PHONE</label>
                      <input type="tel" placeholder="Your phone number" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>EMAIL (OPTIONAL)</label>
                    <input type="email" placeholder="you@example.com" />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>PROPERTY CONDITION</label>
                      <select>
                        <option value="">Select condition</option>
                        <option>Move-in ready</option>
                        <option>Needs minor repairs</option>
                        <option>Needs major repairs</option>
                        <option>Severely distressed</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>TIMELINE TO SELL</label>
                      <select>
                        <option value="">When do you want to...</option>
                        <option>ASAP (within 2 weeks)</option>
                        <option>1–3 months</option>
                        <option>3–6 months</option>
                        <option>Just exploring</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>ANYTHING ELSE? (OPTIONAL)</label>
                    <textarea
                      placeholder="Liens, tenants, inherited, foreclosure, etc."
                      rows={3}
                    ></textarea>
                  </div>
                  <button className="btn-cta">
                    Get My No-Obligation Cash Offer
                  </button>
                  <p className="form-disclaimer">
                    <Shield size={12} strokeWidth={2} /> 100% confidential. No
                    spam. No obligation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section how-it-works" id="how-it-works">
        <div className="container">
          <div className="section-label">HOW IT WORKS</div>
          <h2 className="section-title">Simple. Fast. Done.</h2>
          <p className="section-desc">
            Most homeowners go from first call to cash in hand in under 14 days.
          </p>
          <div className="steps-grid">
            {[
              {
                icon: <Home size={20} color="#fff" strokeWidth={1.75} />,
                num: "01",
                title: "Share your property",
                desc: "Give us your address and a few details online or over the phone. No prep, no showings, no commitment. Takes 60 seconds.",
              },
              {
                icon: <Calculator size={20} color="#fff" strokeWidth={1.75} />,
                num: "02",
                title: "Get your cash offer",
                desc: "Within 24 hours we research your home, account for repairs, and send you a transparent offer. You see every number, no mystery math.",
              },
              {
                icon: <Banknote size={20} color="#fff" strokeWidth={1.75} />,
                num: "03",
                title: "Walk away with cash",
                desc: "Accept and pick a closing date that works for you — as fast as 7 days. We handle the paperwork, cover all costs, and wire cash directly to you.",
              },
            ].map((s) => (
              <div className="step-card" key={s.num}>
                <div className="step-header">
                  <div className="step-icon">{s.icon}</div>
                  <span className="step-num">Step {s.num}</span>
                </div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="cta-row">
            <a href="#hero" className="btn-dark">
              Start with my address →
            </a>
            <span className="cta-note">
              No prep, no showings, no surprises.
            </span>
          </div>
        </div>
      </section>

      {/* <section className="section formula-section" id="formula">
        <div className="container">
          <div className="section-label center">TRANSPARENT OFFER FORMULA</div>
          <h2 className="section-title center">
            Exactly how we calculate your cash offer
          </h2>
          <p className="section-desc center">
            No mystery numbers. We use the same formula every cash buyer uses
            but unlike most, we walk you through it line by line so you know
            your offer is fair before you ever sign anything.
          </p>
          <div className="formula-box">
            <div className="formula-label">THE Solid Offer Buyers CASH OFFER FORMULA</div>
            <div className="formula-equation">
              <span className="formula-pill highlight">Your Offer</span>
              <span className="formula-op">=</span>
              <span className="formula-pill">ARV</span>
              <span className="formula-op">−</span>
              <span className="formula-pill">Repairs</span>
              <span className="formula-op">−</span>
              <span className="formula-pill">Selling Costs</span>
              <span className="formula-op">−</span>
              <span className="formula-pill">Min Return</span>
            </div>
            <p className="formula-note">
              If we agree on a price, that's exactly what you receive at
              closing. No hidden deductions.
              <br />
              No last-minute renegotiations.
            </p>
          </div>
          <div className="formula-cards">
            {[
              {
                tag: "ARV · What your home is worth fixed up",
                icon: <Home size={18} color="#0D1B2A" strokeWidth={1.75} />,
                title: "After Repair Value",
                desc: "What your house could realistically sell for, fully renovated, in today's market based on real comparable sales in your neighborhood.",
              },
              {
                tag: "− COR · What we'll spend fixing it",
                icon: <Wrench size={18} color="#0D1B2A" strokeWidth={1.75} />,
                title: "Cost of Repairs",
                desc: "Everything it takes to bring the property to market-ready condition: roof, kitchen, bath, paint, HVAC, foundation, cosmetics everything.",
              },
              {
                tag: "− COSTS · What it costs to resell it",
                icon: <Receipt size={18} color="#0D1B2A" strokeWidth={1.75} />,
                title: "Selling & Holding Costs",
                desc: "Agent commissions, closing costs, taxes, utilities, insurance, and carrying costs while we own and renovate the home.",
              },
              {
                tag: "− PROFIT · What we earn for taking the risk",
                icon: (
                  <TrendingUp size={18} color="#0D1B2A" strokeWidth={1.75} />
                ),
                title: "Minimum Return",
                desc: "A modest, fixed return that allows Solid Offer Buyers to keep buying houses, paying contractors, and serving homeowners like you.",
              },
            ].map((fc) => (
              <div className="formula-card" key={fc.title}>
                <div className="fc-header">
                  <span className="fc-tag">{fc.tag}</span>
                  <span className="fc-icon">{fc.icon}</span>
                </div>
                <h3>{fc.title}</h3>
                <p>{fc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      <section className="section compare-section" id="compare">
        <div className="container">
          <div className="section-label">COMPARE YOUR OPTIONS</div>
          <h2 className="section-title">
            Selling the traditional way vs. selling to Solid Offer Buyers
          </h2>
          <p className="section-desc">
            Listing with an agent works for some homes. For everything else,
            here's why thousands of homeowners across the country choose Solid
            Offer Buyers instead.
          </p>
          <div className="compare-table">
            <div className="compare-header-row">
              <div className="ch-label">WHAT IT COSTS</div>
              <div className="ch-agent">✕ Selling with an agent</div>
              <div className="ch-Solid Offer Buyers">
                ✓ Selling to Solid Offer Buyers
              </div>
            </div>
            {compareRows.map(([label, bad, good]) => (
              <div className="compare-row" key={label}>
                <div className="cr-label">{label}</div>
                <div className="cr-bad">
                  <span className="cr-dash">—</span> {bad}
                </div>
                <div className="cr-good">
                  <span className="cr-check">✓</span> {good}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section situations-section" id="situations">
        <div className="container">
          <div className="section-label">SITUATIONS WE HELP WITH</div>
          <h2 className="section-title">We buy houses in any situation</h2>
          <p className="section-desc">
            Life happens. Solid Offer Buyers exists to give homeowners across
            the country a real way out fast, fair, and with no judgment.
          </p>
          <div className="situations-grid">
            {situations.map((s) => (
              <div className="situation-card" key={s.title}>
                <div className="sit-icon-wrap">{s.icon}</div>
                <div>
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section testimonials-section">
        <div className="container">
          <div className="section-label">REAL HOMEOWNERS</div>
          <h2 className="section-title">
            What Sellers Say About Working With Us
          </h2>
          <div className="testimonials-grid">
            {testimonials.map((t) => (
              <div className="testimonial-card" key={t.name}>
                <div className="t-top">
                  <div className="t-stars">★★★★★</div>
                  <div className="t-quote-icon">
                    <Quote size={20} strokeWidth={1.5} />
                  </div>
                </div>
                <p className="t-text">{t.text}</p>
                <div className="t-footer">
                  <div>
                    <div className="t-name">{t.name}</div>
                    <div className="t-location">{t.location}</div>
                  </div>
                  <span className="t-tag">{t.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section founder-section">
        <div className="container">
          <div className="founder-inner">
            {/* <div className="founder-photo-col">
              <div className="founder-photo-wrap">
                <img
                  src="/founder.jpeg"
                  alt="Jovan Kekich"
                  className="founder-photo"
                />
              </div>
            </div> */}
            <div className="founder-content">
              <div className="section-label">ABOUT US</div>
              <h2 className="founder-name">Solid Offer Buyers</h2>
              <p className="founder-title">Real Estate Investment Company</p>
              <blockquote className="founder-quote">
                Solid Offer Buyers Properties is a real estate investment
                company that helps homeowners sell their properties quickly,
                fairly, and without the stress of a traditional listing.
              </blockquote>
              <p className="founder-bio">
                With over seven years in business, we have closed multiple
                transactions, purchased many properties, and helped sellers find
                practical solutions for different situations. Whether a property
                needs repairs, is unwanted, or the seller simply wants a
                smoother process, our team provides clear communication, fair
                offers, and a straightforward closing experience.
              </p>
              <p className="founder-bio">
                Our goal is to create win-win solutions for homeowners while
                investing in properties that help improve local communities.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section faqs-section" id="faqs">
        <div className="container">
          <div className="section-label center">FREQUENTLY ASKED</div>
          <h2 className="section-title center">
            Questions homeowners ask before selling to Solid Offer Buyers
          </h2>
          <div className="faqs-list">
            {faqs.map((faq, i) => (
              <div
                className={`faq-item ${faqOpen === i ? "open" : ""}`}
                key={i}
                onClick={() => setFaqOpen(faqOpen === i ? null : i)}
              >
                <div className="faq-question">
                  {faq.q}
                  <span className="faq-chevron">
                    {faqOpen === i ? (
                      <ChevronUp size={16} strokeWidth={2} />
                    ) : (
                      <ChevronDown size={16} strokeWidth={2} />
                    )}
                  </span>
                </div>
                {faqOpen === i && <div className="faq-answer">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-inner">
          <div className="cta-left">
            <div className="section-label light">READY WHEN YOU ARE</div>
            <h2>Get a fair cash offer on your house this week.</h2>
            <p>
              No fees. No repairs. No agents. No pressure.
              <br />
              Just a real homeowner talking to a real
              <br />
              cash buyer who will pick up the phone every time.
            </p>
            <div className="cta-buttons">
              <a href="#hero-form" className="btn-outline-white">
                Submit address online →
              </a>
            </div>
            <div className="cta-stats">
              <div className="cta-stat">
                <div className="cs-num">20+</div>
                <div className="cs-label">Years buying houses</div>
              </div>
              <div className="cta-stat">
                <div className="cs-num">250+</div>
                <div className="cs-label">Homes purchased</div>
              </div>
              <div className="cta-stat">
                <div className="cs-num">24h</div>
                <div className="cs-label">Average offer time</div>
              </div>
            </div>
          </div>
          <div className="cta-right">
            <div className="cta-form-card">
              <div className="cta-form-label">
                QUICK OFFER ·{" "}
                <span style={{ color: "#F5A623" }}>60 SECONDS</span>
              </div>
              <h3>Tell us about your property</h3>
              <div className="form-group">
                <label>PROPERTY ADDRESS</label>
                <input type="text" placeholder="123 Main St, Dallas, TX" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>YOUR NAME</label>
                  <input type="text" placeholder="Full name" />
                </div>
                <div className="form-group">
                  <label>PHONE</label>
                  <input type="tel" placeholder="Your phone number" />
                </div>
              </div>
              <button className="btn-cta">
                Get My No-Obligation Cash Offer
              </button>
              <p className="form-disclaimer">
                <Shield size={12} strokeWidth={2} /> 100% confidential. No spam.
                No obligation.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <img
              src="/logo.png"
              alt="Solid Offer Buyers Properties"
              className="footer-logo-img"
            />
            <p>
              Get top cash offers for your home. Compare multiple offers, sell
              fast, and close on your timeline with no repairs needed.
            </p>
            <div className="footer-contact">
              <div>
                <Phone size={14} strokeWidth={2} /> (216) 666-3609
              </div>
              <div>
                <Mail size={14} strokeWidth={2} /> info@solidofferbuyers.com · ali@solidofferbuyers.com
              </div>
              <div>
                <MapPin size={14} strokeWidth={2} /> Nationwide Coverage · All
                50 States
              </div>
            </div>
          </div>
          <div className="footer-nav-col">
            <div className="footer-col-title">SITE</div>
            <a href="#how-it-works">How It Works</a>
            {/* <a href="#formula">Cash Offer Formula</a> */}
            <a href="#compare">Compare</a>
            <a href="#situations">Situations</a>
            <a href="#faqs">FAQs</a>
            <a href="/privacy.html">Privacy Policy</a>
            <a href="/terms.html">Terms of Service</a>
          </div>
          {/* <div className="footer-areas-col">
            <div className="footer-col-title">WE BUY NATIONWIDE</div>
            <div className="footer-cities">
              {["New York, NY","Los Angeles, CA","Chicago, IL","Houston, TX","Phoenix, AZ","Dallas, TX","Austin, TX","Miami, FL","Atlanta, GA","Seattle, WA","Denver, CO","Nashville, TN","Las Vegas, NV","Portland, OR"].map((c) => (
                <span className="city-tag" key={c}>{c}</span>
              ))}
            </div>
            <p className="footer-cities-note">...and everywhere in the U.S. Submit your address we buy in all 50 states.</p>
          </div> */}
        </div>
        <div className="footer-bottom">
          <span>
            © 2023 Solid Offer Buyers Properties · solidofferbuyers.com. All
            rights reserved. ·{" "}
            <a href="/privacy.html" style={{ color: "inherit", textDecoration: "underline" }}>
              Privacy Policy
            </a>{" "}
            ·{" "}
            <a href="/terms.html" style={{ color: "inherit", textDecoration: "underline" }}>
              Terms of Service
            </a>
          </span>
          <span className="footer-disclaimer">
            Disclaimer: Solid Offer Buyers is a real estate investment company,
            not a licensed real estate broker. Cash offers are based on property
            condition, comparable sales, and market data. Offers are non-binding
            until signed.
          </span>
        </div>
      </footer>
    </div>
  );
};

export default App;
